import React, { useState, useEffect, useRef } from 'react';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';

const FocusAreaScrollIndicator = ({ children, items }) => {
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);
    const scrollRef = useRef(null);

    const checkScroll = () => {
        const element = scrollRef.current;
        if (!element) return;
        
        const { scrollTop, scrollHeight, clientHeight } = element;
        setCanScrollUp(scrollTop > 5);
        setCanScrollDown(scrollTop < scrollHeight - clientHeight - 5);
    };

    useEffect(() => {
        const element = scrollRef.current;
        if (element) {
            checkScroll(); // Initial check
            element.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
            
            // Check after a short delay to ensure content is rendered
            setTimeout(checkScroll, 100);
        }
        
        return () => {
            if (element) {
                element.removeEventListener('scroll', checkScroll);
                window.removeEventListener('resize', checkScroll);
            }
        };
    }, [items]);

    const scrollUp = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ top: -100, behavior: 'smooth' });
        }
    };

    const scrollDown = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ top: 100, behavior: 'smooth' });
        }
    };

    return (
        <div className="relative">
            {/* Top arrow button - Outside on top, centered */}
            {canScrollUp && (
                <button
                    onClick={scrollUp}
                    className="absolute -top-8 left-1/2 -translate-x-1/2 z-10 cursor-pointer hover:scale-110 transition-transform duration-200 flex items-center justify-center w-8 h-8 bg-[#E68A40]/10 rounded-full shadow-lg"
                    aria-label="Scroll up"
                >
                    <FaChevronUp className="text-white text-sm" />
                </button>
            )}
            
            {/* Scrollable list - scrollbar hidden */}
            <ul 
                ref={scrollRef}
                className="space-y-2 text-xs sm:text-sm md:text-base text-gray-200 list-disc list-inside leading-relaxed max-h-[300px] overflow-y-auto"
                style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitScrollbar: { display: 'none' }
                }}
            >
                <style>{`
                    ul::-webkit-scrollbar {
                        display: none;
                    }
                `}</style>
                {items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                ))}
            </ul>
            
            {/* Bottom arrow button - Outside on bottom, centered */}
            {canScrollDown && (
                <button
                    onClick={scrollDown}
                    className="absolute -bottom-8 left-1/2 -translate-x-1/2 z-10 cursor-pointer hover:scale-110 transition-transform duration-200 flex items-center justify-center w-8 h-8 bg-[#E68A40]/10 rounded-full shadow-lg"
                    aria-label="Scroll down"
                >
                    <FaChevronDown className="text-white text-sm" />
                </button>
            )}
        </div>
    );
};

export default FocusAreaScrollIndicator;
